import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Star } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { boardDashboardService } from '../../../services/board-dashboard';
import type {
  BoardDashboardClimb,
  BoardDashboardClimbDetail,
  BoardDashboardUser,
} from '../../../types/board-dashboard';

interface BoardClimbHistoryDialogProps {
  gymSlug: string;
  boardId: string;
  /** The problem to show; null keeps the dialog shut. */
  climb: BoardDashboardClimb | null;
  onClose: () => void;
}

/** Whoever did the thing, or "someone at the wall" when nobody was signed in. */
function Who({ user, anonymousLabel }: { user: BoardDashboardUser | null; anonymousLabel: string }) {
  if (!user) {
    return <span className="text-sm italic text-muted-foreground">{anonymousLabel}</span>;
  }

  return (
    <span className="flex min-w-0 items-center gap-2">
      <Avatar className="size-6 shrink-0">
        {user.avatar_small_url && <AvatarImage src={user.avatar_small_url} alt="" />}
        <AvatarFallback className="text-[10px]">
          {user.name?.slice(0, 2).toUpperCase() ?? '??'}
        </AvatarFallback>
      </Avatar>
      <span className="truncate text-sm">{user.name || user.username}</span>
    </span>
  );
}

/**
 * Everything behind one problem's numbers: who sent it and what they made of
 * it, every attempt logged on it, every time it went up on the wall and every
 * time it was shared.
 *
 * Fetched when it opens rather than carried by the list — a page of rows would
 * otherwise ship a gym-year of history nobody scrolls.
 */
export function BoardClimbHistoryDialog({
  gymSlug,
  boardId,
  climb,
  onClose,
}: BoardClimbHistoryDialogProps) {
  const { t, i18n } = useTranslation();
  const [detail, setDetail] = useState<BoardDashboardClimbDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const climbId = climb?.id ?? null;

  useEffect(() => {
    if (!climbId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetail(null);

    boardDashboardService
      .getClimbDetail(gymSlug, boardId, climbId)
      .then((loaded) => {
        if (!cancelled) setDetail(loaded);
      })
      .catch((err: unknown) => {
        // Raw message, translated at render: `t` in the dependencies would
        // re-fetch on every identity of the translation object.
        if (!cancelled) setError(err instanceof Error ? err.message : '');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [gymSlug, boardId, climbId]);

  const when = (iso: string) =>
    new Date(iso).toLocaleString(i18n.language, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const anonymous = t('gymAdmin.boardDashboard.history.anonymous');
  const counts = {
    ascents: detail?.ascents.length ?? 0,
    attempts: detail?.attempts.length ?? 0,
    lightings: detail?.lightings.length ?? 0,
    shares: detail?.shares.length ?? 0,
  };

  return (
    <Dialog open={climb !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="truncate">{climb?.name}</DialogTitle>
          <DialogDescription>
            {climb?.inclination === null || climb?.inclination === undefined
              ? t('gymAdmin.boardDashboard.history.subtitle')
              : t('gymAdmin.boardDashboard.history.subtitleAtAngle', { value: climb.inclination })}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex justify-center py-12">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {error !== null && (
          <p className="py-8 text-center text-sm text-destructive">
            {error || t('errors.unexpectedError')}
          </p>
        )}

        {detail && (
          <Tabs defaultValue="ascents">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="ascents">
                {t('gymAdmin.boardDashboard.history.ascents')} ({counts.ascents})
              </TabsTrigger>
              <TabsTrigger value="attempts">
                {t('gymAdmin.boardDashboard.history.attempts')} ({counts.attempts})
              </TabsTrigger>
              <TabsTrigger value="lightings">
                {t('gymAdmin.boardDashboard.history.lightings')} ({counts.lightings})
              </TabsTrigger>
              <TabsTrigger value="shares">
                {t('gymAdmin.boardDashboard.history.shares')} ({counts.shares})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="ascents">
              <ScrollArea className="h-80">
                {detail.ascents.length === 0 ? (
                  <p className="py-10 text-center text-sm text-muted-foreground">
                    {t('gymAdmin.boardDashboard.history.noAscents')}
                  </p>
                ) : (
                  <ul className="divide-y">
                    {detail.ascents.map((ascent) => (
                      <li key={ascent.id} className="flex items-center gap-3 py-2 pr-3">
                        <Who user={ascent.user} anonymousLabel={anonymous} />
                        <span className="ml-auto flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                          {ascent.rating !== null && (
                            <span className="inline-flex items-center gap-0.5 tabular-nums">
                              <Star className="size-3" aria-hidden />
                              {ascent.rating.toFixed(1)}
                            </span>
                          )}
                          {ascent.attempts_count !== null && (
                            <span className="tabular-nums">
                              {t('gymAdmin.boardDashboard.history.tries', {
                                count: ascent.attempts_count,
                              })}
                            </span>
                          )}
                          <Badge variant={ascent.topped ? 'default' : 'secondary'}>
                            {ascent.topped
                              ? (ascent.send_type ??
                                t('gymAdmin.boardDashboard.history.sent'))
                              : t('gymAdmin.boardDashboard.history.projecting')}
                          </Badge>
                          <span>{when(ascent.sent_at ?? ascent.logged_at)}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </ScrollArea>
            </TabsContent>

            <TabsContent value="attempts">
              <ScrollArea className="h-80">
                {detail.attempts.length === 0 ? (
                  <p className="py-10 text-center text-sm text-muted-foreground">
                    {t('gymAdmin.boardDashboard.history.noAttempts')}
                  </p>
                ) : (
                  <ul className="divide-y">
                    {detail.attempts.map((attempt) => (
                      <li key={attempt.id} className="flex items-center gap-3 py-2 pr-3">
                        <Who user={attempt.user} anonymousLabel={anonymous} />
                        <span className="ml-auto flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                          {attempt.attempt_type && (
                            <Badge variant="outline">{attempt.attempt_type}</Badge>
                          )}
                          <span>{when(attempt.attempted_at)}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </ScrollArea>
            </TabsContent>

            <TabsContent value="lightings">
              <ScrollArea className="h-80">
                {detail.lightings.length === 0 ? (
                  <p className="py-10 text-center text-sm text-muted-foreground">
                    {t('gymAdmin.boardDashboard.history.noLightings')}
                  </p>
                ) : (
                  <ul className="divide-y">
                    {detail.lightings.map((lighting) => (
                      <li key={lighting.id} className="flex items-center gap-3 py-2 pr-3">
                        <Who user={lighting.user} anonymousLabel={anonymous} />
                        <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                          {when(lighting.lit_at)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </ScrollArea>
            </TabsContent>

            <TabsContent value="shares">
              <ScrollArea className="h-80">
                {detail.shares.length === 0 ? (
                  <p className="py-10 text-center text-sm text-muted-foreground">
                    {t('gymAdmin.boardDashboard.history.noShares')}
                  </p>
                ) : (
                  <ul className="divide-y">
                    {detail.shares.map((share) => (
                      <li key={share.id} className="flex items-center gap-3 py-2 pr-3">
                        <Who user={share.user} anonymousLabel={anonymous} />
                        <span className="ml-auto flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                          {share.media_type && <Badge variant="outline">{share.media_type}</Badge>}
                          <span>{when(share.shared_at)}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </ScrollArea>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
